import React, { useState } from "react";
import { FiX, FiAlertCircle } from "react-icons/fi";

interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  reviewPrompt: string;
  proposedArgs: Record<string, any>;
  onAccept: () => void;
  onReject: (reason: string) => void;
  onEdit: (args: Record<string, any>) => void;
  isLoading?: boolean;
}

/**
 * Modal component for reviewing and approving agent actions
 */
const ReviewModal: React.FC<ReviewModalProps> = ({
  isOpen,
  onClose,
  reviewPrompt,
  proposedArgs,
  onAccept,
  onReject,
  onEdit,
  isLoading = false,
}) => {
  const [activeTab, setActiveTab] = useState<"accept" | "reject" | "edit">(
    "accept"
  );
  const [rejectReason, setRejectReason] = useState("");
  const [editedArgs, setEditedArgs] = useState(
    JSON.stringify(proposedArgs, null, 2)
  );
  const [editError, setEditError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleAccept = () => {
    onAccept();
  };

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(rejectReason);
      setRejectReason("");
    }
  };

  const handleEdit = () => {
    try {
      const parsedArgs = JSON.parse(editedArgs);
      setEditError(null);
      onEdit(parsedArgs);
      setEditedArgs("");
    } catch (error) {
      setEditError("Invalid JSON format. Please check your input.");
    }
  };

  const isEditValid = () => {
    try {
      JSON.parse(editedArgs);
      return editedArgs.trim() !== "";
    } catch {
      return false;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md animate-fadeIn p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col animate-scaleIn border border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-purple-50 to-blue-50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-600 rounded-xl">
              <FiAlertCircle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Review Required</h2>
              <p className="text-sm text-gray-600 mt-0.5">Please review and approve the proposed action</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all disabled:opacity-50"
            disabled={isLoading}
            title="Close"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-8">
          {/* Review Prompt */}
          <div className="mb-6 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
            <p className="text-gray-800 leading-relaxed font-medium">{reviewPrompt}</p>
          </div>

          {/* Proposed Changes */}
          <div className="mb-6">
            <h3 className="text-base font-bold text-gray-800 mb-3 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
              Proposed Changes
            </h3>
            <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 border border-gray-200 shadow-sm overflow-x-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono leading-relaxed">
                {JSON.stringify(proposedArgs, null, 2)}
              </pre>
            </div>
          </div>

          {/* Action Tabs */}
          <div className="border-b-2 border-gray-100 mb-6">
            <div className="flex gap-2">
              <button
                className={`pb-3 px-4 font-semibold transition-all relative ${
                  activeTab === "accept"
                    ? "text-green-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                } rounded-t-lg`}
                onClick={() => setActiveTab("accept")}
              >
                <span className="flex items-center gap-2">
                  ✓ Accept
                </span>
                {activeTab === "accept" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-green-500 to-green-600 rounded-full"></div>
                )}
              </button>
              <button
                className={`pb-3 px-4 font-semibold transition-all relative ${
                  activeTab === "reject"
                    ? "text-red-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                } rounded-t-lg`}
                onClick={() => setActiveTab("reject")}
              >
                <span className="flex items-center gap-2">
                  ✕ Reject
                </span>
                {activeTab === "reject" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 to-red-600 rounded-full"></div>
                )}
              </button>
              <button
                className={`pb-3 px-4 font-semibold transition-all relative ${
                  activeTab === "edit"
                    ? "text-blue-700"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                } rounded-t-lg`}
                onClick={() => setActiveTab("edit")}
              >
                <span className="flex items-center gap-2">
                  ✎ Edit
                </span>
                {activeTab === "edit" && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full"></div>
                )}
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="min-h-[120px]">
            {activeTab === "accept" && (
              <div className="p-6 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center">
                      <span className="text-white text-lg">✓</span>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-green-900 mb-1">Ready to proceed</h4>
                    <p className="text-green-800 text-sm">Click "Accept" to approve and execute the proposed changes.</p>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "reject" && (
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Reason for rejection (required)
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please explain why you're rejecting this action..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-100 min-h-[120px] transition-all"
                  disabled={isLoading}
                />
              </div>
            )}

            {activeTab === "edit" && (
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  Edit the proposed arguments (JSON format)
                </label>
                <textarea
                  value={editedArgs}
                  onChange={(e) => {
                    setEditedArgs(e.target.value);
                    setEditError(null);
                  }}
                  placeholder="Enter valid JSON..."
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100 font-mono text-sm min-h-[220px] bg-gray-50 transition-all"
                  disabled={isLoading}
                />
                {editError && (
                  <div className="mt-3 p-3 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                    <p className="text-sm text-red-700 font-medium">{editError}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-8 py-5 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <button
            onClick={onClose}
            className="px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all font-medium"
            disabled={isLoading}
          >
            Cancel
          </button>

          <div className="flex gap-3">
            {activeTab === "accept" && (
              <button
                onClick={handleAccept}
                className="px-8 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl hover:from-green-700 hover:to-green-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-semibold active:scale-95 transform"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  "✓ Accept & Execute"
                )}
              </button>
            )}

            {activeTab === "reject" && (
              <button
                onClick={handleReject}
                className="px-8 py-2.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-semibold active:scale-95 transform"
                disabled={isLoading || !rejectReason.trim()}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  "✕ Reject Action"
                )}
              </button>
            )}

            {activeTab === "edit" && (
              <button
                onClick={handleEdit}
                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 font-semibold active:scale-95 transform"
                disabled={isLoading || !isEditValid()}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Processing...
                  </span>
                ) : (
                  "✎ Submit Edit"
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;
